import { INTERNAL_NOTE_CHANNEL_ID } from "../../../components/MessageHistory/ChannelSelector";
import i18n from "../../../i18n/config";

export default function useMessageHandler({
  selectedConversation,
  userData,
  setMessages,
  sendMessageMutation,
  updateMutation,
  onUpdateConversation,
}) {
  const getSendErrorMessage = (error) => {
    const code = String(error?.code || "").toLowerCase();
    const status = Number(error?.status || error?.statusCode || 0);
    if (status === 402 || code.startsWith("billing.")) {
      if (code === "billing.limit_disabled") {
        return i18n.t("billingErrors.limitDisabled", { ns: "app" });
      }
      if (typeof error?.message === "string" && error.message.trim()) {
        return error.message;
      }
      return i18n.t("billingErrors.limitExceeded", { ns: "app" });
    }
    return null;
  };

  const addOptimisticMessage = (
    tempId,
    { message, file, audioURL, channelType, channel, isImageFile, localImageUrl }
  ) => {
    const hasAudio = !!audioURL;
    const isInternalNoteDraft = String(channel) === String(INTERNAL_NOTE_CHANNEL_ID);
    const base = {
      id: tempId,
      text: message || (file ? file.name || "Arquivo enviado" : ""),
      senderName: userData?.nome,
      senderId: userData?.id,
      timestamp: new Date().toISOString(),
      file: file ? file.name : null,
      audio: hasAudio ? audioURL : null,
      // Para anexos enviados via anotação interna, a UI deve renderizar como "note"
      // (mantendo o layout de anotação) mesmo enquanto ainda está "sending".
      type: hasAudio ? "audio" : isInternalNoteDraft ? "note" : channelType || "note",
      status: "sending",
      lastUpdate: new Date().toISOString(),
      channel: channelType,
    };
    const enriched =
      isImageFile && localImageUrl
        ? isInternalNoteDraft
          ? { ...base, image: localImageUrl }
          : { ...base, type: "image", image: localImageUrl }
        : { ...base, text: base.text || "Arquivo enviado" };
    setMessages((prev) => {
      const updated = new Map(prev);
      updated.set(tempId, enriched);
      return updated;
    });
  };

  const handleSendMessage = async (payload, retryId = null) => {
    if (!selectedConversation?.id) return;

    const { message, file, files, audioURL, channel, channelType, transferTo, templateId, emailTo, emailSubject, emailCc } = payload;
    const filesList = Array.isArray(files) && files.length > 0 ? files : file ? [file] : [];

    if (transferTo && updateMutation?.mutateAsync) {
      try {
        // Importante: ao transferir para agente, NÃO enviar `id_usuario` (mesmo como `null`),
        // para que o backend não interprete como "remoção explícita de atribuição do usuário".
        // O mesmo vale para transferir para usuário (não enviar `id_agente`).
        await updateMutation.mutateAsync({
          id: selectedConversation.id,
          ...(transferTo.type === "user" ? { id_usuario: transferTo.id } : { id_agente: transferTo.id }),
        });
        const updates =
          transferTo.type === "user"
            ? {
                userId: transferTo.id,
                id_agente: null,
                agentId: null,
                agentName: null,
                userName: transferTo.label ?? null,
                assignedTo: transferTo.label ?? null,
              }
            : {
                userId: null,
                id_agente: transferTo.id,
                agentId: transferTo.id,
                agentName: transferTo.label ?? null,
                userName: null,
                assignedTo: null,
              };
        onUpdateConversation?.(updates);
      } catch (err) {
        console.error("Erro ao transferir solicitação:", err);
      }
    }

    const baseTime = Date.now();
    const sendItems = [];
    if (message != null && String(message).trim()) {
      sendItems.push({ type: "text", message: message.trim() });
    }
    filesList.forEach((f) => sendItems.push({ type: "file", file: f }));
    if (audioURL) {
      sendItems.push({ type: "audio", audioURL });
    }

    if (sendItems.length === 0) return;

    const applyTemplateAndTransfer = (index) =>
      index === 0
        ? {
            ...(transferTo && { transferTo }),
            ...(templateId != null && { templateId }),
            ...(emailTo != null && { emailTo }),
            ...(emailSubject != null && { emailSubject }),
            ...(emailCc != null && { emailCc }),
          }
        : {};

    for (let i = 0; i < sendItems.length; i++) {
      const item = sendItems[i];
      const tempId = `temp-${baseTime}-${i}`;

      if (retryId && i === 0) {
        setMessages((prev) => {
          const updated = new Map(prev);
          updated.delete(retryId);
          return updated;
        });
      }

      const isImageFile =
        item.type === "file" &&
        item.file &&
        typeof item.file.type === "string" &&
        item.file.type.startsWith("image/");
      const localImageUrl = isImageFile && item.file ? URL.createObjectURL(item.file) : null;

      addOptimisticMessage(tempId, {
        message: item.type === "text" ? item.message : "",
        file: item.type === "file" ? item.file : null,
        audioURL: item.type === "audio" ? item.audioURL : null,
        channelType,
        channel,
        isImageFile,
        localImageUrl,
      });

      try {
        const mutationPayload = {
          conversationId: selectedConversation.id,
          phone: selectedConversation?.celular || "",
          email: selectedConversation?.email || "",
          message: item.type === "text" ? item.message : "",
          file: item.type === "file" ? item.file : undefined,
          audioURL: item.type === "audio" ? item.audioURL : undefined,
          channel,
          projectId: selectedConversation.projectId,
          userId: userData?.id,
          ...applyTemplateAndTransfer(i),
        };
        const result = await sendMessageMutation.mutateAsync(mutationPayload);
        const resultData = result?.data ?? result ?? {};

        setMessages((prev) => {
          const updated = new Map(prev);
          const original = updated.get(tempId);
          if (!original) return prev;
          if (localImageUrl) URL.revokeObjectURL(localImageUrl);
          const newId = resultData.id ?? resultData.insertedId ?? resultData.messageId ?? resultData?.data?.id;
          const createdAt = resultData.createdAt ?? null;
          const updatedAt = resultData.updatedAt ?? null;
          const deliveryStatus = resultData.deliveryStatus ?? null;
          if (!newId) {
            // fallback: mantém tempId, mas marca como enviado para não ficar preso em "Enviando..."
            updated.set(tempId, { ...original, status: "sent", createdAt: createdAt || original.createdAt, updatedAt: updatedAt || original.updatedAt, deliveryStatus: deliveryStatus || original.deliveryStatus });
            return updated;
          }
          updated.set(newId, { ...original, id: newId, status: "sent", createdAt: createdAt || original.createdAt, updatedAt: updatedAt || original.updatedAt, deliveryStatus: deliveryStatus || original.deliveryStatus });
          updated.delete(tempId);
          return updated;
        });
      } catch (error) {
        console.error("Erro ao enviar mensagem:", error);
        // Não revoga localImageUrl para manter o preview visível na mensagem com status "error"
        const friendlyError = getSendErrorMessage(error);
        setMessages((prev) => {
          const updated = new Map(prev);
          const errored = updated.get(tempId);
          if (!errored) return prev;
          updated.set(tempId, {
            ...errored,
            status: "error",
            errorMessage: friendlyError || errored.errorMessage || null,
          });
          return updated;
        });
      }
    }
  };

  return { handleSendMessage };
}
